import { useState } from "react";
import { card, btn, btnGhost, label, inputStyle, Row, SelectRow, DOMAIN_LABELS } from "./_genCommon";

/** Step 1: Configure constraints + objective weights. */
export default function ConfigureStep({ domain, cfg, onDomainChange,
                                                          onCfgChange, onGenerate }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const update = (k, v) => onCfgChange({ ...cfg, [k]: v });
  const updateW = (k, v) => onCfgChange({ ...cfg, objective_weights: {
        ...cfg.objective_weights, [k]: parseFloat(v) || 0 } });

  return (
    <div data-testid="gen-configure" style={{ display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
      {/* Domain */}
      <div style={{ ...card, padding: 14 }}>
        <div style={label}>Domain</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {Object.keys(DOMAIN_LABELS).map(d => (
            <button key={d} data-testid={`gen-domain-${d}`}
                style={{ ...btnGhost, padding: 10, textAlign: "left",
                            ...(domain === d ? { borderColor: "#F26B1F", color: "#F26B1F" } : {}) }}
                onClick={() => onDomainChange(d)}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>{DOMAIN_LABELS[d].label}</div>
              <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 2 }}>
                {DOMAIN_LABELS[d].desc}
              </div>
            </button>
          ))}
        </div>
        <div style={{ ...label, marginTop: 16 }}># Alternatives to generate</div>
        <input data-testid="gen-num-alts" type="number" min="3" max="50"
            style={{ ...inputStyle, marginTop: 6 }}
            value={cfg.num_alternatives}
            onChange={(e) => update("num_alternatives", parseInt(e.target.value) || 8)} />
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--pf-muted)" }}>
          First N alternatives are pre-named strategies (Lowest Cost, Max Reliability,
          Premium, Greenest, etc.). Beyond that, additional variants explore the
          design space.
        </div>
      </div>

      {/* Constraints */}
      <div style={{ ...card, padding: 14 }}>
        <div style={label}>Constraints</div>
        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
          <Row k="Budget min (INR cr)" v={cfg.budget_min_INR_cr}
                fk="budget_min_INR_cr" update={update} />
          <Row k="Budget max (INR cr)" v={cfg.budget_max_INR_cr}
                fk="budget_max_INR_cr" update={update} />
          <Row k="Max construction (months)" v={cfg.max_construction_months}
                fk="max_construction_months" update={update} />
          {domain === "process_plant" && (
            <Row k="Feed rate (MTPD)" v={cfg.feed_rate_MTPD}
                  fk="feed_rate_MTPD" update={update} />
          )}
          {domain === "infrastructure" && (<>
            <Row k="Length (km)" v={cfg.length_km} fk="length_km" update={update} />
            <Row k="Design speed (km/h)" v={cfg.design_speed_kmh}
                  fk="design_speed_kmh" update={update} />
            <Row k="Lanes" v={cfg.lanes} fk="lanes" update={update} />
            <SelectRow k="Terrain" v={cfg.terrain} fk="terrain" update={update}
                  opts={["flat", "rolling", "hilly", "mountainous"]} />
          </>)}
          {domain === "real_estate" && (<>
            <Row k="Plot area (m²)" v={cfg.plot_area_m2}
                  fk="plot_area_m2" update={update} />
            <Row k="FSI allowed" v={cfg.fsi_allowed}
                  fk="fsi_allowed" update={update} />
            <Row k="Max floors" v={cfg.max_floors}
                  fk="max_floors" update={update} />
            <SelectRow k="Building type" v={cfg.building_type}
                  fk="building_type" update={update}
                  opts={["residential", "commercial", "mixed_use"]} />
          </>)}
        </div>
      </div>

      {/* Objective weights */}
      <div style={{ ...card, padding: 14 }}>
        <div style={label}>Objective Weights (sum ≈ 1.0)</div>
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--pf-muted)" }}>
          Slide to prioritise. Weights renormalise on the backend.
        </div>
        <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
          {Object.entries(cfg.objective_weights).map(([k, v]) => (
            <div key={k}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span>{k.replace(/_/g, " ")}</span>
                <b style={{ color: "#F26B1F" }}>{(v * 100).toFixed(0)} %</b>
              </div>
              <input data-testid={`gen-w-${k}`} type="range" min="0" max="1" step="0.05"
                  value={v} onChange={(e) => updateW(k, e.target.value)}
                  style={{ width: "100%", accentColor: "#F26B1F" }} />
            </div>
          ))}
        </div>
        <button data-testid="gen-start" style={{ ...btn, marginTop: 16, width: "100%" }}
            onClick={onGenerate}>
          Generate {cfg.num_alternatives} Alternatives →
        </button>
      </div>

      {/* Advanced (full width) */}
      <div style={{ gridColumn: "1 / -1", ...card, padding: 14 }}>
        <button data-testid="gen-toggle-advanced"
            style={{ ...btnGhost, width: "100%", textAlign: "left", padding: 8 }}
            onClick={() => setShowAdvanced(!showAdvanced)}>
          {showAdvanced ? "▼" : "▶"} Advanced constraints (per-domain)
        </button>
        {showAdvanced && (
          <div style={{ marginTop: 12, display: "grid",
                            gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {domain === "process_plant" && <PPAdvanced cfg={cfg} update={update} />}
            {domain === "infrastructure" && <InfraAdvanced cfg={cfg} update={update} />}
            {domain === "real_estate" && <REAdvanced cfg={cfg} update={update} />}
          </div>
        )}
      </div>
    </div>
  );
}

function PPAdvanced({ cfg, update }) {
  return <>
    <div>
      <div style={label}>Utilities Available</div>
      <Row k="Steam tph max" v={cfg.utilities?.steam_tph || 0}
            fk="utilities" update={(k, v) => update("utilities",
                  { ...cfg.utilities, steam_tph: parseFloat(v) || 0 })} />
      <Row k="CW supply T (°C)" v={cfg.utilities?.cw_C || 32}
            fk="utilities" update={(k, v) => update("utilities",
                  { ...cfg.utilities, cw_C: parseFloat(v) || 0 })} />
      <Row k="Power kW max" v={cfg.utilities?.power_kW || 0}
            fk="utilities" update={(k, v) => update("utilities",
                  { ...cfg.utilities, power_kW: parseFloat(v) || 0 })} />
    </div>
    <div>
      <div style={label}>Environmental Limits</div>
      <Row k="CO₂ t/yr cap" v={cfg.environmental_limits?.CO2_tpy || 0}
            fk="env" update={(k, v) => update("environmental_limits",
                  { ...cfg.environmental_limits, CO2_tpy: parseFloat(v) || 0 })} />
      <Row k="Effluent m³/d cap" v={cfg.environmental_limits?.effluent_m3d || 0}
            fk="env" update={(k, v) => update("environmental_limits",
                  { ...cfg.environmental_limits, effluent_m3d: parseFloat(v) || 0 })} />
      <SelectRow k="Preferred technology" v={cfg.preferred_technology || "any"}
            fk="preferred_technology" update={update}
            opts={["any", "fluid_catalytic", "hydrocracking", "alkylation",
                      "polymerization", "olefins"]} />
    </div>
  </>;
}

function InfraAdvanced({ cfg, update }) {
  return <>
    <div>
      <div style={label}>Geo + structures</div>
      <Row k="ROW width (m)" v={cfg.rou_width_m} fk="rou_width_m" update={update} />
      <SelectRow k="Pavement preference" v={cfg.pavement_preference || "any"}
            fk="pavement_preference" update={update}
            opts={["any", "flexible", "rigid", "composite", "flexible_CTB"]} />
      <Row k="Avoid zones (count)" v={(cfg.avoid_zones || []).length}
            fk="_az" update={(k, v) => update("avoid_zones",
                  Array.from({ length: parseInt(v) || 0 },
                      (_, i) => ({ name: `Zone-${i + 1}`, polygon: [] })))} />
      <Row k="River crossings (count)" v={(cfg.river_crossings || []).length}
            fk="_rc" update={(k, v) => update("river_crossings",
                  Array.from({ length: parseInt(v) || 0 },
                      (_, i) => ({ chainage_km: 5 + i * 6 })))} />
    </div>
    <div>
      <div style={label}>Codes (auto-loaded)</div>
      <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 4 }}>
        {(cfg.codes || ["IS", "IRC", "MoRTH"]).join(" · ")}
      </div>
    </div>
  </>;
}

function REAdvanced({ cfg, update }) {
  return <>
    <div>
      <div style={label}>Setbacks (m)</div>
      <Row k="Front" v={cfg.setbacks?.front_m || 6}
            fk="setbacks" update={(k, v) => update("setbacks",
                  { ...cfg.setbacks, front_m: parseFloat(v) || 0 })} />
      <Row k="Side"  v={cfg.setbacks?.side_m || 3}
            fk="setbacks" update={(k, v) => update("setbacks",
                  { ...cfg.setbacks, side_m: parseFloat(v) || 0 })} />
      <Row k="Rear"  v={cfg.setbacks?.rear_m || 4.5}
            fk="setbacks" update={(k, v) => update("setbacks",
                  { ...cfg.setbacks, rear_m: parseFloat(v) || 0 })} />
      <Row k="Max height (m)" v={cfg.max_height_m || 60}
            fk="max_height_m" update={update} />
      <Row k="Landscape %" v={cfg.landscape_pct} fk="landscape_pct" update={update} />
    </div>
    <div>
      <div style={label}>Unit mix + green target</div>
      <Row k="1 BHK count" v={cfg.unit_mix?.["1BHK"] || 0}
            fk="unit_mix" update={(k, v) => update("unit_mix",
                  { ...cfg.unit_mix, "1BHK": parseInt(v) || 0 })} />
      <Row k="2 BHK count" v={cfg.unit_mix?.["2BHK"] || 0}
            fk="unit_mix" update={(k, v) => update("unit_mix",
                  { ...cfg.unit_mix, "2BHK": parseInt(v) || 0 })} />
      <Row k="3 BHK count" v={cfg.unit_mix?.["3BHK"] || 0}
            fk="unit_mix" update={(k, v) => update("unit_mix",
                  { ...cfg.unit_mix, "3BHK": parseInt(v) || 0 })} />
      <Row k="Parking / unit" v={cfg.parking_per_unit}
            fk="parking_per_unit" update={update} />
      <SelectRow k="IGBC target" v={cfg.igbc_target || "none"}
            fk="igbc_target" update={update}
            opts={["none", "gold", "platinum"]} />
      <SelectRow k="Structural pref." v={cfg.structural_preference || "any"}
            fk="structural_preference" update={update}
            opts={["any", "conventional_beam_slab", "flat_slab_drop_panel",
                      "flat_slab_no_drop", "shear_wall_flat_slab", "precast_hybrid"]} />
    </div>
  </>;
}
